import { useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { PART_REQUEST_STATUS, type PartDocType, type PartRequestStatus } from '@etnrlz/shared';
import { usePartRequests, useUpdatePartRequestAny } from '../../lib/queries';
import { Badge, Button, Loading, Select } from '../../components/ui';
import { Icon } from '../../components/icons';
import { partRequestStatusMeta } from '../../components/status';
import { PartLogList } from '../../components/PartLog';
import { PartRequestModal, StockModal } from './OrdersPage';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { carrierTrackingUrl } from '../../lib/tracking';

const docLabel: Record<PartDocType, string> = {
  INVOICE: 'Invoice (fornecedor)',
  NOTA_FISCAL: 'Nota fiscal de entrada',
  OUTRO: 'Outro',
};

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-sm text-right">{children}</span>
    </div>
  );
}

/**
 * Tela do pedido da peça: mostra todos os dados de uma peça solicitada, a OS de
 * origem (com link para abrir em outra aba) e troca rápida de status.
 */
export function PartRequestDetailPage() {
  const { osId, prId } = useParams();
  const { pathname } = useLocation();
  const base = pathname.startsWith('/shop') ? '/shop' : '/admin';
  const osHref = base === '/shop' ? `/shop?os=${osId}` : `/admin/orders?os=${osId}`;
  const comprasHref = `${base}/compras`;
  const { data, isLoading } = usePartRequests();
  const update = useUpdatePartRequestAny();
  const [editing, setEditing] = useState(false);
  const [stocking, setStocking] = useState(false);
  const pr = useMemo(
    () => (data ?? []).find((p) => p.id === prId && p.osId === osId),
    [data, prId, osId],
  );

  if (isLoading) return <Loading />;
  if (!pr) {
    return (
      <div>
        <Link to={comprasHref} className="text-sm text-accent hover:underline">
          ← Compras
        </Link>
        <p className="text-sm text-muted mt-4">Peça não encontrada.</p>
      </div>
    );
  }

  const meta = partRequestStatusMeta[pr.status];
  const subtotal = (pr.valor ?? 0) * (pr.quantidade ?? 1);
  const totalPedido = pr.valor != null ? subtotal + (pr.frete ?? 0) : undefined;
  const trackUrl = pr.codigoRastreio
    ? carrierTrackingUrl(pr.transportadora, pr.codigoRastreio)
    : undefined;

  return (
    <div className="max-w-3xl">
      <Link to={comprasHref} className="text-sm text-accent hover:underline">
        ← Compras
      </Link>

      <div className="flex items-start gap-4 mt-3 mb-4">
        <div className="h-20 w-20 rounded-lg bg-surface-2 overflow-hidden grid place-items-center shrink-0 border border-border">
          {pr.fotoUrl ? (
            <img src={pr.fotoUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <Icon name="box" className="h-7 w-7 text-muted" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold leading-tight">
            {pr.link ? (
              <a
                href={pr.link}
                target="_blank"
                rel="noreferrer noopener"
                className="hover:text-accent hover:underline"
                title="Abrir link da peça em outra aba"
              >
                {pr.titulo ?? 'Peça'}
              </a>
            ) : (
              (pr.titulo ?? 'Peça sem título')
            )}
          </h1>
          {pr.descricao && <p className="text-sm text-muted mt-1">{pr.descricao}</p>}
          <div className="mt-2">
            <Badge color={meta.color}>{meta.label}</Badge>
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button variant="secondary" onClick={() => setEditing(true)}>
            <Icon name="wrench" className="h-4 w-4" /> Editar
          </Button>
          {pr.status === 'ENTREGUE' &&
            (pr.emEstoque ? (
              <span className="text-xs text-st-ok inline-flex items-center justify-center gap-1">
                <Icon name="box" className="h-4 w-4" /> No estoque
              </span>
            ) : (
              <Button onClick={() => setStocking(true)}>
                <Icon name="box" className="h-4 w-4" /> Em estoque
              </Button>
            ))}
        </div>
      </div>

      <PartRequestModal
        osId={pr.osId}
        open={editing}
        initial={pr}
        onClose={() => setEditing(false)}
      />

      <StockModal osId={pr.osId} pr={pr} open={stocking} onClose={() => setStocking(false)} />

      {/* OS de origem — abre em outra aba */}
      <div className="card p-3 mb-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-muted">Solicitada na OS</div>
          <div className="font-medium truncate">{pr.osTitulo}</div>
          <div className="text-xs text-muted">{pr.vehicleApelido ?? pr.vehicleId}</div>
        </div>
        <a href={osHref} target="_blank" rel="noreferrer noopener" className="shrink-0">
          <Button variant="secondary">
            <Icon name="link" className="h-4 w-4" /> Abrir OS
          </Button>
        </a>
      </div>

      {/* Troca rápida de status */}
      <div className="card p-3 mb-4">
        <label className="text-xs uppercase tracking-wide text-muted">Status</label>
        <Select
          className="mt-1"
          value={pr.status}
          disabled={update.isPending}
          onChange={(e) =>
            update.mutate({
              osId: pr.osId,
              prId: pr.id,
              input: { status: e.target.value as PartRequestStatus },
            })
          }
        >
          {PART_REQUEST_STATUS.map((s) => (
            <option key={s} value={s}>
              {partRequestStatusMeta[s].label}
            </option>
          ))}
        </Select>
      </div>

      {/* Pedido & valores */}
      <div className="card p-4 mb-4">
        <div className="text-xs uppercase tracking-wide text-muted mb-2">Pedido & valores</div>
        {pr.orderId && (
          <Row label="OrderID">
            <span className="num">#{pr.orderId}</span>
          </Row>
        )}
        <Row label="Quantidade">
          <span className="num">{pr.quantidade}</span>
        </Row>
        {pr.valor != null && (
          <Row label="Valor unitário">
            <span className="num">{formatMoney(pr.valor, pr.moeda)}</span>
          </Row>
        )}
        {pr.valor != null && pr.quantidade > 1 && (
          <Row label="Subtotal">
            <span className="num">{formatMoney(subtotal, pr.moeda)}</span>
          </Row>
        )}
        {pr.frete != null && pr.frete > 0 && (
          <Row label="Frete">
            <span className="num">{formatMoney(pr.frete, pr.moeda)}</span>
          </Row>
        )}
        {totalPedido != null && (
          <Row label="Total do pedido">
            <span className="num font-medium">{formatMoney(totalPedido, pr.moeda)}</span>
          </Row>
        )}
        {pr.valorEntregue != null && (
          <Row label="Valor entregue (no Brasil)">
            <span className="num">{formatMoney(pr.valorEntregue)}</span>
          </Row>
        )}
      </div>

      {/* Envio & rastreio */}
      {(pr.transportadora ||
        pr.codigoRastreio ||
        pr.prazoEntrega ||
        pr.rastreioStatus ||
        pr.lojaStatus) && (
        <div className="card p-4 mb-4">
          <div className="text-xs uppercase tracking-wide text-muted mb-2">Envio & rastreio</div>
          {pr.lojaStatus && (
            <Row label="Status na loja">
              {pr.lojaStatus}
              {pr.lojaStatusEm && (
                <span className="text-muted text-xs ml-2">{formatDateTime(pr.lojaStatusEm)}</span>
              )}
            </Row>
          )}
          {pr.transportadora && <Row label="Transportadora">{pr.transportadora}</Row>}
          {pr.codigoRastreio && (
            <Row label="Código de rastreio">
              <span className="inline-flex items-center gap-2">
                <span className="num">{pr.codigoRastreio}</span>
                {trackUrl && (
                  <a
                    href={trackUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-accent hover:underline inline-flex items-center gap-1"
                    title="Rastrear na transportadora (ao vivo)"
                  >
                    <Icon name="link" className="h-3.5 w-3.5" /> Rastrear
                  </a>
                )}
              </span>
            </Row>
          )}
          {pr.rastreioStatus && <Row label="Situação">{pr.rastreioStatus}</Row>}
          {pr.rastreioAtualizadoEm && (
            <Row label="Atualizado">{formatDateTime(pr.rastreioAtualizadoEm)}</Row>
          )}
          {pr.prazoEntrega && <Row label="Previsão de entrega">{formatDate(pr.prazoEntrega)}</Row>}
        </div>
      )}

      {/* Histórico & origem (log de proveniência) */}
      <div className="card p-4 mb-4">
        <div className="text-xs uppercase tracking-wide text-muted mb-3">Histórico & origem</div>
        <PartLogList entries={pr.historico ?? []} />
      </div>

      {/* Documentos */}
      {(pr.documentos?.length ?? 0) > 0 && (
        <div className="card p-4 mb-4">
          <div className="text-xs uppercase tracking-wide text-muted mb-2">Documentos</div>
          <ul className="space-y-1 text-sm">
            {pr.documentos!.map((d, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="text-xs text-muted shrink-0">{docLabel[d.tipo]}:</span>
                <a
                  href={d.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-accent hover:underline truncate"
                >
                  {d.nome ?? 'arquivo'}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pr.observacoes && (
        <div className="card p-4 mb-4">
          <div className="text-xs uppercase tracking-wide text-muted mb-1">Observações</div>
          <p className="text-sm whitespace-pre-wrap">{pr.observacoes}</p>
        </div>
      )}

      <div className="text-xs text-muted">
        Criada em {formatDateTime(pr.criadaEm)}
        {pr.atualizadaEm ? ` · atualizada em ${formatDateTime(pr.atualizadaEm)}` : ''}
      </div>
    </div>
  );
}
